/* eslint-disable react-hooks/exhaustive-deps */
import { FilterMultiSelectorProps } from '@access-erp/common';
import { AdvancedFilterCustomRenderProps, DynamicDataMultiselect } from '@access-erp/ui-components';
import { useEffect, useMemo, useState } from 'react';

import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';

const UploadedByMultiSelector = ({
  title,
  value,
  onFilterValueChanged,
  name,
  disabled,
}: FilterMultiSelectorProps<string[]>): React.JSX.Element => {
  const { searchUsers, isLoadingSearchUsers } = useSearchUsers();
  const [searchValue, setSearchValue] = useState<string>();
  const [users, setUsers] = useState<User[]>([]);

  const searchedUsers = useMemo(() => {
    if (searchValue)
      return users.filter((user) => user.FirstName.toLocaleLowerCase().includes(searchValue)
        || user.LastName.toLocaleLowerCase().includes(searchValue));
    else return users;
  }, [searchValue, users]);

  useEffect(() => {
    const getUsers = async () => {
      const searchResult = await searchUsers({ searchName: searchValue });
      setUsers(searchResult);
    };

    getUsers();
  }, []);

  const getLabel = (selectedId?: string) => {
    if (selectedId) {
      const user = users.find((activeUser) => activeUser.Id === selectedId);
      return user ? `${user.FirstName} ${user.LastName}` : 'Unknown User';
    }
    return 'Unknown User';
  };

  return (
    <DynamicDataMultiselect
      key={`${title}-selector`}
      autocompleteProps={{
        disabled,
        dataTestId: name,
        filterValueChanged: (selected) => {
          const stringValues = selected
            .map((item) =>
              typeof item === 'string'
                ? item
                : typeof item === 'object' && item !== null && 'value' in item
                  ? String((item as { value: string | number }).value)
                  : ''
            )
            .filter((v) => v !== '');
          onFilterValueChanged(stringValues as string[]);
        },
        dataFetching: {
          dataMapper: (data: User[]) => {
            return data?.map((item) => ({
              ...item,
              id: item.Id,
              value: item.Id,
              label: `${item.FirstName} ${item.LastName}`,
            }));
          },
          useGetData: (_, search) => {
            setSearchValue(search);
            return {
              data: searchedUsers,
              loading: isLoadingSearchUsers,
            };
          },
        },
        value:
          value?.map((userId) => ({
            id: userId,
            label: getLabel(userId) ?? 'Unknown User',
            value: userId,
          })) ?? [],
      }}
    />
  );
};

export const renderUploadedBy = ({
  name,
  onFilterValueChanged,
  title,
  value,
}: AdvancedFilterCustomRenderProps<string[]>): React.JSX.Element => (
  <UploadedByMultiSelector
    name={name}
    title={title}
    key="uploadedBy"
    value={value}
    onFilterValueChanged={onFilterValueChanged}
  />
);

export default UploadedByMultiSelector;
